import { createColumnHelper } from '@tanstack/react-table';

import { formatterCurrency } from '@/utilities/format';

import { isCurrencyISO } from '@/constants/currencies';

import type { PortfolioDetailedReturns } from '../../hooks/usePortfolioDetailedReturns';

import { HeaderCell } from './HeaderCell';
import { TypeCell } from './TypeCell';

import { DateEnGB } from './constants';

const tranactionType = ['buy', 'sell', 'dividend', 'split'] as const;
type TranactionType = (typeof tranactionType)[number];

export const isType = (type: string): type is TranactionType =>
  tranactionType.includes(type as TranactionType);

type Holding = PortfolioDetailedReturns['holdings'][number];
type Transaction = Holding['detailedTransactions'][number];

const columnHelper = createColumnHelper<Transaction>();

type Args = {
  portfolioCurrencyISO: unknown;
  holdingCurrencyISO: unknown;
};

export const getTransactionsColumns = ({
  portfolioCurrencyISO,
  holdingCurrencyISO,
}: Args) => {
  const portfolioISO = isCurrencyISO(portfolioCurrencyISO)
    ? portfolioCurrencyISO
    : 'XXX';
  const holdingISO = isCurrencyISO(holdingCurrencyISO)
    ? holdingCurrencyISO
    : 'XXX';

  return [
    columnHelper.accessor((row) => row, {
      header: 'Type',
      cell: (cell) => <TypeCell {...cell.getValue()} />,
    }),
    columnHelper.accessor('transactionDate', {
      header: 'Date',
      cell: (cell) => DateEnGB.format(new Date(cell.getValue())),
    }),
    columnHelper.accessor((row) => row, {
      header: 'Shares',
      cell: (cell) => {
        const { type, units, startingUnits } = cell.getValue();
        if (isType(type) && type === 'buy') return units;
        if (isType(type) && type === 'split') {
          return `${startingUnits} → ${units}`;
        }
        return units;
      },
    }),
    columnHelper.accessor((row) => row, {
      header: () => <HeaderCell label="Price" currencyISO={holdingISO} />,
      id: 'price',
      cell: (cell) => {
        const { type, amount } = cell.getValue();
        if (isType(type) && type === 'split') return null;
        return formatterCurrency(amount, 'XXX');
      },
    }),
    columnHelper.accessor((row) => row, {
      header: () => <HeaderCell label="Cashflow" currencyISO={portfolioISO} />,
      id: 'cashflow',
      cell: (cell) => {
        const { type, changeInCash } = cell.getValue();
        if (isType(type) && type === 'split') return null;
        return formatterCurrency(changeInCash, 'XXX');
      },
    }),
  ];
};
